'use client';

import React from 'react';
import { Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotificationCenter } from '@/providers/NotificationCenterProvider';

import { NotificationModal } from './NotificationModal';

export interface NotificationBellProps {
    className?: string;
    iconClassName?: string;
    badgeClassName?: string;
}

export function NotificationBell({ className, iconClassName, badgeClassName }: NotificationBellProps) {
    const { unreadCount, isDrawerOpen, openDrawer, closeDrawer } = useNotificationCenter();

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isDrawerOpen) {
            closeDrawer();
        } else {
            openDrawer();
        }
    };

    const defaultButtonClass = `relative rounded-xl transition-all duration-300 group flex items-center justify-center ${isDrawerOpen
        ? 'bg-hprimary-50 text-hprimary-600'
        : 'hover:bg-gradient-to-br hover:from-hprimary-50 hover:to-hprimary-100/50 text-gray-700'
        }`;

    const defaultIconClass = `w-6 h-6 transition-colors duration-300 ${isDrawerOpen ? 'text-hprimary-600 bg-gray-100 rounded-lg w-5 h-5 transition-all duration-300' : 'group-hover:text-hprimary-600'
        }`;

    const defaultBadgeClass = "absolute -top-3 -right-4 min-w-[20px] h-5 bg-gradient-to-br from-red-500 to-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1.5 shadow-lg shadow-red-500/40 border-2 border-white";

    return (
        <div className="relative">
            <motion.button
                id="notification-bell-button"
                onClick={handleToggle}
                onMouseDown={(e) => e.stopPropagation()}
                className={className || defaultButtonClass}
                aria-label="Notifications"
                whileHover={isDrawerOpen ? {} : { scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
            >
                {/* Bell Icon */}
                <Bell
                    className={iconClassName || defaultIconClass}
                    strokeWidth={2}
                />

                {/* Unread Badge */}
                <AnimatePresence>
                    {unreadCount > 0 && (
                        <>
                            <motion.span
                                initial={{ scale: 0, rotate: -180 }}
                                animate={{ scale: 1, rotate: 0 }}
                                exit={{ scale: 0, rotate: 180 }}
                                transition={{
                                    type: 'spring',
                                    damping: 15,
                                    stiffness: 300
                                }}
                                className={badgeClassName || defaultBadgeClass}
                            >
                                {unreadCount > 20 ? '20+' : unreadCount}
                            </motion.span>
                        </>
                    )}
                </AnimatePresence>
            </motion.button>

            {/* Notification Dropdown */}
            <NotificationModal />
        </div>
    );
}
